import React, { useState } from 'react';
import { Platform, Text } from 'react-native';
import { useDispatch } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';

import { Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  Button,
  Stack,
  HeaderText,
  BodyText,
} from '../../../../../ui';
import { InsightCardProps, ISnackbarOpenProps } from '../types';
import { IRootDispatch } from '../../../../../../store/models';
import { SITE_ROOT } from '../../../../../../config';
import { DismissButton } from './DismissButton';
import TimeToComplete from './TimeToComplete';

export const BASE_ID = 'marketing-tips-marquee-card';

const InlineHeadlineMarqueeCardWithSnackbar: React.FC<InsightCardProps> = ({
  id,
  headline,
  subhead,
  headlineColor,
  subheadColor,
  bodyText,
  cta,
  clickThroughData,
  analyticsData,
  onClick,
  onAction,
  firstViewTracking,
  dismissParams,
  snackBarProps,
  timeToComplete,
}) => {
  const dispatch = useDispatch<IRootDispatch>();
  const instanceId = `${BASE_ID}-${id}`;
  const [hasTracked, setHasTracked] = useState(false);

  const showSnackbar = async () => {
    const snackbarProps: ISnackbarOpenProps = {
      subHeader: snackBarProps.subHeader,
      icon: snackBarProps.icon || '',
      sideColor: snackBarProps.color || colors.blackInBlack,
      timeOut: snackBarProps.timeOut,
    };

    // open snackbar
    dispatch.snackbar.open(snackbarProps);
  };

  const copyToClipboard = () => {
    if (Platform.OS !== 'web') {
      return;
    }

    // document.execCommand copies value from a focused input field
    const textArea = document.createElement('textarea');
    // set value to vanity url
    textArea.value = `${SITE_ROOT}/${snackBarProps.vanityUrl}`;
    textArea.style.display = 'hidden';
    // focus on textarea to copy to clipboard
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    // copy vanity url to clipboard
    if (document.execCommand('copy')) {
      // if success, show snackbar
      showSnackbar();
    }

    document.body.removeChild(textArea);
  };

  const onClickHandler = async () => {
    onClick(clickThroughData, analyticsData, onAction);
    copyToClipboard();
  };

  const visibilityChangeHandler = isVisible => {
    if (hasTracked) {
      return;
    }

    if (isVisible && firstViewTracking) {
      setHasTracked(true);
      firstViewTracking?.();
    }
  };

  return (
    <VisibilitySensor onChange={visibilityChangeHandler}>
      <Box
        padding={4}
        testID={instanceId}
      >
        <Stack
          alignItems="center"
          paddingY={24}
          paddingX={24}
          space={8}
          showBoxShadow
          color={colors.whiteWash}
          minHeight={260}
          justifyContent="space-evenly"
        >
          <Stack
            alignItems="center"
            borderRadius={4}
            paddingX={24}
          >
            <Box paddingBottom={16}>
              <TimeToComplete
                testId={`${instanceId}-time-to-complete`}
                time={timeToComplete}
              />
            </Box>
            <Box maxWidth="100%">
              <Text style={{ textAlign: 'center' }}>
                <HeaderText
                  level={2}
                  textAlign="center"
                  testID={`${instanceId}-headline`}
                  color={headlineColor || colors.blackInBlack}
                  style={{ width: '100%' }}
                >
                  {headline}
                </HeaderText>
                <HeaderText
                  level={2}
                  textAlign="center"
                  testID={`${instanceId}-subhead`}
                  color={subheadColor || colors.blackInBlack}
                >
                  {' '}
                  {subhead}
                </HeaderText>
              </Text>
            </Box>
            <Box maxWidth="100%" paddingTop={4}>
              <BodyText
                appearance="medium"
                textAlign="center"
                testID={`${instanceId}-subtext`}
              >
                {bodyText?.map(({ color, text }, index) => (
                  <BodyText
                    key={text}
                    appearance="medium"
                    color={color || colors.blackInBlack}
                    textAlign="center"
                  >
                    {index > 0 && (' ')}
                    {text}
                  </BodyText>
                ))}
              </BodyText>
            </Box>
          </Stack>
          <Button
            size="small"
            onClick={onClickHandler}
            testID={`${instanceId}-cta`}
          >
            {cta}
          </Button>
          {dismissParams && (
            <DismissButton {...dismissParams} testID={`${instanceId}-dismiss`} />
          )}
        </Stack>
      </Box>
    </VisibilitySensor>
  );
};

export default InlineHeadlineMarqueeCardWithSnackbar;
